import {createAsyncThunk} from '@reduxjs/toolkit';
import {CLDispatch, RootState} from 'common/src/redux/store';
import {AssetFolderEntityType} from 'common/src/modules/Assets/Assets.enums';
import {createAssetUtil} from 'common/src/modules/Assets/store/shared/assetDisplayThunkUtils';
import getCurrentFolder from 'common/src/modules/Assets/store/shared/utils/getCurrentFolder';
import {refreshCurrentFolder} from 'common/src/modules/Assets/store/shared/thunks/refreshCurrentFolder';
import {
  setAssetTree,
  setIsLoadingFolderAssets,
} from 'common/src/modules/Assets/store/assetsLibrary/assetsLibrary.slice';

interface CreateAssetParams {
  files: File[];
}

/**
 * Create assets in the current folder
 * After creation, refreshes the current folder contents
 * @returns Array of created asset IDs
 */
export const createTemplateAsset = createAsyncThunk<
  number[],
  CreateAssetParams,
  {dispatch: CLDispatch; state: RootState}
>('templateAssets/createTemplateAsset', async ({files}, {dispatch, getState, rejectWithValue}) => {
  try {
    const state = getState();
    const assetTree = state.creativeTemplatePage.templateAssets.assetTree;
    const currentFolderId = state.creativeTemplatePage.templateAssets.currentFolderId;

    if (!assetTree || currentFolderId === null) {
      throw new Error('Asset tree or current folder not found. Please load asset tree first.');
    }

    const currentFolder = getCurrentFolder(assetTree, currentFolderId);

    if (!currentFolder) {
      throw new Error('Current folder not found in tree');
    }

    // Create assets using shared utility (handles S3 upload + job waiting)
    const assetIds = await createAssetUtil(
      AssetFolderEntityType.CreativeTemplate,
      currentFolderId,
      files,
    );

    // Refresh current folder contents once after all jobs complete
    await dispatch(
      refreshCurrentFolder({
        selectAssetTree: (state: RootState) => state.creativeTemplatePage.templateAssets.assetTree,
        selectCurrentFolderId: (state: RootState) =>
          state.creativeTemplatePage.templateAssets.currentFolderId,
        setAssetTree,
        setIsLoadingFolderAssets,
        entityType: AssetFolderEntityType.CreativeTemplate,
      }),
    );

    return assetIds;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to create assets';

    return rejectWithValue(errorMessage);
  }
});
